import { ApiError, RequestAbortError, type AccountEvent, type ApiClient } from '../api/client.js';

export interface AccountEvents {
  subscribe(listener: (event: AccountEvent) => void): () => void;
  /** Opens the account's live stream and keeps it open (reconnecting with backoff) until stop(). */
  start(api: Pick<ApiClient, 'streamEvents'>): void;
  stop(): void;
}

/** One live stream per signed-in app window; views subscribe instead of polling for parallel tasks. */
export function createAccountEvents(options: { wait?: (ms: number, signal: AbortSignal) => Promise<void> } = {}): AccountEvents {
  const listeners = new Set<(event: AccountEvent) => void>();
  let ctl: AbortController | null = null;
  const wait = options.wait ?? ((ms, signal) => new Promise<void>(resolve => {
    const timer = setTimeout(done, ms);
    function done() { clearTimeout(timer); signal.removeEventListener('abort', done); resolve(); }
    signal.addEventListener('abort', done, { once: true });
  }));
  async function run(api: Pick<ApiClient, 'streamEvents'>, signal: AbortSignal): Promise<void> {
    let backoff = 2000;
    while (!signal.aborted) {
      const opened = Date.now();
      try { await api.streamEvents(event => { for (const listener of [...listeners]) { try { listener(event); } catch { /* one view never stops the rest */ } } }, signal); }
      catch (error) {
        if (signal.aborted || error instanceof RequestAbortError) return;
        // Signed out, or a server without the stream: nothing to retry.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 404)) return;
        if (error instanceof ApiError && error.status === 429) backoff = 60_000;
      }
      if (Date.now() - opened > 60_000) backoff = 2000;
      await wait(backoff, signal);
      backoff = Math.min(backoff * 2, 60_000);
    }
  }
  return {
    subscribe(listener) { listeners.add(listener); return () => { listeners.delete(listener); }; },
    start(api) {
      if (ctl || typeof api.streamEvents !== 'function') return;
      const mine = new AbortController(); ctl = mine;
      void run(api, mine.signal).finally(() => { if (ctl === mine) ctl = null; });
    },
    stop() { ctl?.abort(); ctl = null; },
  };
}

export const accountEvents = createAccountEvents();

/** «Terminado: …» for a parallel task; voice tasks say which unit asked for them. Never shows the result itself. */
export function taskFinishedText(event: AccountEvent): string | null {
  if (event.type === 'desktop.plan_requested') return null;
  if (event.type !== 'task.finished' || event.task.status === 'cancelled') return null;
  const title = event.task.title?.trim() || 'tu encargo';
  const from = event.task.origin === 'device' ? ' (pedido a ASIS One)' : '';
  if (event.task.status === 'succeeded') return `Terminado: ${title}${from}. Lo tienes en Actividad.`;
  if (event.task.status === 'needs_input') return `${title}${from} necesita que le respondas. Míralo en Actividad.`;
  return `No se pudo terminar: ${title}${from}.`;
}
